// src/api/aiApi.js
import apiClient from "./apiClient";

// AI generation calls can take well over the default 30s axios timeout,
// especially on a cold backend or with the larger Plan My Day prompt
// (BLOCKED slots + todo-batching rules). Override the timeout for just
// these endpoints — 120s — so the request isn't killed mid-generation.
// Other API calls keep the snappy global default.
const AI_TIMEOUT_MS = 120000;

// Generate AI focus brief — Anthropic API key never leaves the backend
export async function generateBrief(promptData) {
  const response = await apiClient.post("/api/ai/brief", promptData, { timeout: AI_TIMEOUT_MS });
  // rawPrompt mode returns { text } instead of { brief }
  return response.data.text || response.data.brief;
}

// AI day planning — returns raw JSON text for the Plan My Day feature
export async function planDay(prompt) {
  const response = await apiClient.post("/api/ai/plan-day", { prompt }, { timeout: AI_TIMEOUT_MS });
  return response.data.text;
}

// Extract to-dos from meeting notes
export async function extractTodos(notesText, meetingContext) {
  const response = await apiClient.post("/api/ai/extract-todos", {
    notesText,
    meetingContext,
  }, { timeout: AI_TIMEOUT_MS });
  return response.data.todos;
}

// Fetch email chase-up analysis — inbox + sent last 10 days
// Returns { inboxItems, sentItems } — two separate lists.
// atCompanies/atResources sent so backend can DLP-scrub known names before AI sees them.
export async function fetchChaseUp({ atCompanies = [], atResources = [] } = {}) {
  const response = await apiClient.post("/api/emails/chase-up", {
    atCompanies: atCompanies.map(c => ({ id: c.id, companyName: c.companyName })),
    atResources: atResources.map(r => ({ firstName: r.firstName, lastName: r.lastName, name: r.name })),
  }, { timeout: AI_TIMEOUT_MS });
  return {
    inboxItems: response.data.inboxItems || [],
    sentItems:  response.data.sentItems  || [],
  };
}

// Fetch Client Health Check — per-client, per-ticket AI analysis
// managedCompanyIds: array of AT company IDs to analyse
// dattoAlerts/dattoSites: from frontend dattoData prop for Datto correlation
export async function fetchClientHealth({ managedCompanyIds = [], dattoAlerts = [], dattoSites = [] } = {}) {
  const response = await apiClient.post("/api/focus/client-health", {
    managedCompanyIds,
    dattoAlerts,
    dattoSites,
  }, { timeout: AI_TIMEOUT_MS }); // 2 min — parallel AI calls for all clients
  return response.data.clients || [];
}
